import { useCallback } from "react";
import {
  extractDroppedFiles,
  isEmptySelection,
  type UploadSelection,
} from "../utils/uploadSelection";
import { hasExternalFiles, isInsideModal } from "../utils/dragEvents";
import { useDragHighlight } from "./useDragHighlight";

export interface UseExternalFileDropOptions {
  /** Called with what was dropped (files, their relative paths, folders) once resolved. */
  onFiles: (selection: UploadSelection) => void;
  /** When true, drops are ignored and no highlight is shown. */
  disabled?: boolean;
}

export interface ExternalFileDropResult {
  /** True while external files are being dragged over the zone. */
  isDragOver: boolean;
  /** Spread onto the drop-zone element. */
  dropZoneProps: {
    onDrop: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
  };
}

/**
 * Page-level drag-and-drop upload for OS files. Activates ONLY for external
 * file drags (`dataTransfer.types` contains "Files"), so it never interferes
 * with in-app drag-to-move of documents/folders (which carry custom MIME
 * types). Returns an `isDragOver` flag for highlight styling and props to
 * spread onto the drop zone.
 */
export function useExternalFileDrop({
  onFiles,
  disabled = false,
}: UseExternalFileDropOptions): ExternalFileDropResult {
  const highlight = useDragHighlight();
  const { enter, leave, clear } = highlight;

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !hasExternalFiles(e.dataTransfer) || isInsideModal(e.target)) return;
      e.preventDefault();
      enter();
    },
    [disabled, enter]
  );

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (!hasExternalFiles(e.dataTransfer) || isInsideModal(e.target)) return;
      e.preventDefault();
      leave();
    },
    [leave]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      // A drop released inside a modal belongs to that modal: without this,
      // dropping a file into the "add new version" dialog would also fire the
      // page handler, which re-reads the same file as a brand-new upload.
      if (disabled || !hasExternalFiles(e.dataTransfer) || isInsideModal(e.target)) return;
      e.preventDefault();
      clear();
      // Resolve synchronously-captured DataTransfer entries, then hand off.
      extractDroppedFiles(e.dataTransfer)
        .then((selection) => {
          if (!isEmptySelection(selection)) onFiles(selection);
        })
        .catch((error) => {
          console.error("Failed to read dropped files:", error);
        });
    },
    [disabled, onFiles, clear]
  );

  return { isDragOver: highlight.isActive, dropZoneProps: { onDrop, onDragOver, onDragLeave } };
}
